import type { ReactNode } from 'react'
import { CheckIcon, GlobeIcon, ListIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import type { AssistRoute } from './assist.types'

type CopilotSearchFilterProps = {
  value: ReadonlySet<AssistRoute>
  onToggle: (category: AssistRoute) => void
  /** False when the reader cannot fix the Backlog side themselves. */
  backlogAvailable?: boolean
  /** The sentence that says why, shown on the dimmed button. */
  backlogUnavailableReason?: string | null
}

/**
 * The Web search / Backlog chips (BRAI-712).
 *
 * Each toggles on its own and the parent keeps at least one on. They tell the
 * AI service which routes to work — a route turned off costs nothing to run,
 * rather than being answered and then hidden. They began as a view filter
 * over the suggestion list as well, and no longer are: what the service has
 * already raised stays on screen.
 *
 * In a shared room the server refuses the frame, so pressing one changes
 * nothing. They are left where they are rather than hidden, so the panel does
 * not rearrange itself the moment two machines merge.
 *
 * A Backlog the reader cannot repair is dimmed and still pressable: the state
 * is theirs, and a ticket store that comes back should not cost them a second
 * press.
 */
export default function CopilotSearchFilter({
  value,
  onToggle,
  backlogAvailable = true,
  backlogUnavailableReason = null,
}: CopilotSearchFilterProps) {
  const { t } = useUiStrings()

  return (
    <div
      role="group"
      aria-label={t.copilotSearchFilterLabel}
      data-testid="copilot-search-filter"
      className="flex flex-wrap items-center gap-1.5"
    >
      <FilterChip
        label={t.copilotFilterWeb}
        selected={value.has('websearch')}
        testId="copilot-search-filter-web"
        icon={<GlobeIcon className="h-3 w-3" />}
        onSelect={() => onToggle('websearch')}
      />
      <FilterChip
        label={t.copilotFilterBacklog}
        selected={value.has('backlog')}
        testId="copilot-search-filter-backlog"
        icon={<ListIcon className="h-3 w-3" />}
        onSelect={() => onToggle('backlog')}
        dimmed={!backlogAvailable}
        title={backlogAvailable ? undefined : backlogUnavailableReason ?? undefined}
      />
    </div>
  )
}

function FilterChip({
  label,
  selected,
  testId,
  icon,
  onSelect,
  dimmed = false,
  title,
}: {
  label: string
  selected: boolean
  testId: string
  icon: ReactNode
  onSelect: () => void
  dimmed?: boolean
  title?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      data-testid={testId}
      onClick={onSelect}
      title={title}
      className={[
        'inline-flex h-7 items-center gap-1 rounded-lg border bg-white px-2 text-xs font-medium leading-none shadow-[0px_1px_1px_rgba(0,0,0,0.05)]',
        selected
          ? 'border-[#3b82f6] text-[#2563eb]'
          : 'border-[#e2e8f0] text-[#64748b] hover:bg-slate-50 dark:border-[var(--border-color)] dark:text-[var(--text-secondary)] dark:hover:bg-white/5',
        'dark:bg-[var(--bg-tertiary)]',
        // Dimmed, not disabled: the route is still the reader's to choose, and
        // the server answers what it can rather than refusing.
        dimmed ? 'opacity-50' : '',
      ].join(' ')}
    >
      {icon}
      {label}
      {selected ? <CheckIcon className="h-3 w-3" /> : null}
    </button>
  )
}
